import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, API_BASE, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, CheckCircle2, Truck, Beaker, FileCheck, IndianRupee, XCircle, Loader2, ShieldCheck, CalendarClock, ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import FollowUpsPanel from "@/components/bookings/FollowUpsPanel";

const FLOW = [
  { id: "booked", label: "Booked", icon: CheckCircle2 },
  { id: "scheduled", label: "Collection scheduled", icon: Calendar },
  { id: "sample_collected", label: "Sample collected", icon: Beaker },
  { id: "in_transit", label: "In transit to lab", icon: Truck },
  { id: "at_lab", label: "Processing at lab", icon: Beaker },
  { id: "report_ready", label: "Report ready", icon: FileCheck },
  { id: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const CANCELLABLE = new Set(["booked", "scheduled"]);
const RESCHEDULABLE = new Set(["booked", "scheduled"]);
const SLOT_TIMES = ["07:00", "08:00", "09:00", "10:00", "11:00", "17:00", "18:00"];

export default function PatientBookings() {
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("tab");
    return p === "followups" ? "followups" : "marketplace";
  });

  const load = () => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout role="patient">
      <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Calendar className="w-6 h-6 text-efgh-orange" /> My bookings</h1>
      <p className="text-ink/60">Marketplace bookings and follow-up requests from your Smart Reports.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList data-testid="patient-bookings-tabs">
          <TabsTrigger value="marketplace" data-testid="tab-marketplace"><ShoppingBag className="w-4 h-4 mr-2" /> Marketplace</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups"><Sparkles className="w-4 h-4 mr-2" /> Follow-ups</TabsTrigger>
        </TabsList>

        <TabsContent value="marketplace" className="mt-5">
          <div className="grid gap-3">
            {bookings.length === 0 && <Card className="p-10 rounded-2xl text-center text-ink/50">No marketplace bookings yet.</Card>}
            {bookings.map((b) => {
              const isCancelled = b.booking_status === "cancelled";
              const stepIdx = Math.max(0, FLOW.findIndex((s) => s.id === b.booking_status));
              const currentLabel = isCancelled ? "Cancelled" : (FLOW[stepIdx]?.label || b.booking_status);
              const canCancel = CANCELLABLE.has(b.booking_status);
              return (
                <Card key={b.id} className={`p-5 rounded-2xl ${isCancelled ? "opacity-70" : ""}`} data-testid={`booking-${b.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-ink/50">Ref · {b.id.slice(0, 8).toUpperCase()}</div>
                      <div className="mt-1 font-heading font-semibold text-ink">{b.patient_name}</div>
                      <div className="text-sm text-ink/60">{b.slot_date} · {b.slot_time} · ₹{b.total_amount}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <span className={`pill ${isCancelled ? "pill-high" : b.booking_status === "delivered" ? "pill-normal" : "pill-borderline"}`}>{currentLabel}</span>
                      {b.refund_status && b.refund_status !== "not_applicable" && (
                        <span className="pill bg-efgh-blue/10 text-efgh-blue text-[11px]">
                          <IndianRupee className="w-3 h-3" /> Refund {b.refund_status.replace("_", " ")}
                        </span>
                      )}
                      <Button variant="outline" onClick={() => setSelected(b)} data-testid={`track-${b.id}`}>Track</Button>
                      {RESCHEDULABLE.has(b.booking_status) && (
                        <Button variant="outline" onClick={() => setRescheduling(b)} data-testid={`reschedule-${b.id}`}>
                          <CalendarClock className="w-4 h-4 mr-1.5" /> Reschedule
                        </Button>
                      )}
                      {canCancel && (
                        <Button variant="outline" onClick={() => setCancelling(b)} data-testid={`cancel-${b.id}`} className="border-red-200 text-red-600 hover:bg-red-50">
                          <XCircle className="w-4 h-4 mr-1.5" /> Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Compact progress bar */}
                  {!isCancelled && (
                    <div className="mt-4 flex items-center gap-1">
                      {FLOW.slice(0, -1).map((_, i) => (
                        <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= stepIdx - 1 || (b.booking_status === "delivered") ? "bg-efgh-green" : "bg-muted"}`} />
                      ))}
                    </div>
                  )}
                  {isCancelled && b.cancel_reason && (
                    <div className="mt-3 text-xs text-ink/60">Reason: {b.cancel_reason}</div>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="followups" className="mt-5">
          <FollowUpsPanel role="patient" />
        </TabsContent>
      </Tabs>

      <TrackDialog booking={selected} onClose={() => setSelected(null)} />
      <CancelDialog
        booking={cancelling}
        onClose={() => setCancelling(null)}
        onDone={() => { setCancelling(null); load(); }}
      />
      <RescheduleDialog
        booking={rescheduling}
        onClose={() => setRescheduling(null)}
        onDone={() => { setRescheduling(null); load(); }}
      />
    </DashboardLayout>
  );
}

function TrackDialog({ booking, onClose }) {
  if (!booking) return null;
  const stepIdx = Math.max(0, FLOW.findIndex((s) => s.id === booking.booking_status));
  const history = booking.status_history || [];
  const upiUrl = (booking.payment_method === "upi" || booking.payment_method === "upi_after") ? `${API_BASE}/upi/qr?vpa=lab@paytm&amount=${booking.total_amount}&note=EFGH-${booking.id.slice(0,8)}` : null;
  const isCancelled = booking.booking_status === "cancelled";
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="track-dialog">
        <DialogTitle className="font-heading">Track your booking</DialogTitle>
        <div className="text-sm text-ink/60">Ref: {booking.id.slice(0, 8).toUpperCase()}</div>

        {isCancelled ? (
          <div className="mt-4 p-4 rounded-xl border border-red-200 bg-red-50/50">
            <div className="font-heading font-semibold text-red-600 flex items-center gap-2"><XCircle className="w-4 h-4" /> Cancelled</div>
            {booking.cancel_reason && <div className="text-sm text-ink/70 mt-1">Reason: {booking.cancel_reason}</div>}
            {booking.refund_status && (
              <div className="text-xs text-ink/60 mt-1">Refund status: <b className="capitalize">{booking.refund_status.replace("_", " ")}</b></div>
            )}
          </div>
        ) : (
          <ol className="mt-5 relative border-l-2 border-border ml-2 space-y-4">
            {FLOW.map((s, i) => {
              const done = i <= stepIdx || booking.booking_status === "delivered";
              const active = i === stepIdx;
              const Icon = s.icon;
              const entry = history.find((h) => h.status === s.id);
              return (
                <li key={s.id} className="relative pl-5" data-testid={`step-${s.id}`}>
                  <div className={`absolute -left-[13px] w-6 h-6 rounded-full grid place-items-center ${done ? "bg-efgh-green text-white" : active ? "bg-efgh-yellow text-ink" : "bg-muted text-ink/40"}`}>
                    <Icon className="w-3 h-3" />
                  </div>
                  <div className={`font-medium ${done ? "text-ink" : "text-ink/50"}`}>{s.label}</div>
                  {entry && <div className="text-xs text-ink/50 mt-0.5">{new Date(entry.at).toLocaleString()}{entry.note ? ` · ${entry.note}` : ""}</div>}
                </li>
              );
            })}
          </ol>
        )}

        {upiUrl && !isCancelled && (
          <div className="mt-5 p-3 rounded-xl border border-border">
            <div className="font-heading font-semibold text-sm text-ink flex items-center gap-2"><IndianRupee className="w-4 h-4" /> Pay via UPI</div>
            <img src={upiUrl} alt="UPI QR" className="mt-2 w-32 h-32 rounded-lg border border-border" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ booking, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [booking?.id]);
  if (!booking) return null;

  const paid = booking.payment_method === "upi" && booking.payment_status === "paid";
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/cancel`, { reason: reason.trim() || "No longer needed" });
      toast.success("Booking cancelled");
      onDone();
    } catch (e) { toast.error(apiError(e, "Cancel failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cancel-dialog">
        <DialogTitle className="font-heading">Cancel booking?</DialogTitle>
        <div className="text-sm text-ink/60">Ref: {booking.id.slice(0, 8).toUpperCase()} · ₹{booking.total_amount}</div>

        <div className="mt-4 p-3 rounded-xl bg-muted/60 text-xs text-ink/70 flex gap-2 items-start">
          <ShieldCheck className="w-4 h-4 text-efgh-green shrink-0 mt-0.5" />
          <div>
            <b className="text-ink">Refund policy</b>
            <ul className="mt-1 space-y-1 list-disc pl-4">
              <li>Pay-at-collection bookings: no charge, no refund needed.</li>
              <li>UPI-paid bookings cancelled before sample pickup: <b>full refund</b> in 5-7 business days.</li>
              <li>After sample collection, please contact the lab directly.</li>
            </ul>
          </div>
        </div>

        {paid && <div className="mt-3 text-xs text-efgh-blue">A refund of ₹{booking.total_amount} will be initiated to your original payment method.</div>}

        <div className="mt-4">
          <Textarea rows={3} placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason" />
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={busy}>Keep booking</Button>
          <Button onClick={submit} disabled={busy} data-testid="cancel-confirm" className="bg-red-600 hover:bg-red-700 text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />} Cancel booking
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function RescheduleDialog({ booking, onClose, onDone }) {
  const [slotDate, setSlotDate] = useState("");
  const [slotTime, setSlotTime] = useState("09:00");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (booking) {
      setSlotDate(booking.slot_date || "");
      setSlotTime(booking.slot_time || "09:00");
    }
  }, [booking?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!booking) return null;

  const submit = async () => {
    if (!slotDate) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/reschedule`, { slot_date: slotDate, slot_time: slotTime });
      toast.success("Slot updated");
      onDone();
    } catch (e) { toast.error(apiError(e, "Reschedule failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reschedule-dialog">
        <DialogTitle className="font-heading">Reschedule pickup</DialogTitle>
        <div className="text-sm text-ink/60">Ref: {booking.id.slice(0, 8).toUpperCase()}</div>

        <div className="mt-4 p-3 rounded-xl bg-muted/60 text-xs text-ink/70">
          Current slot: <b className="text-ink">{booking.slot_date} · {booking.slot_time}</b>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div>
            <Label>New date</Label>
            <Input type="date" data-testid="reschedule-date" value={slotDate} onChange={(e) => setSlotDate(e.target.value)} />
          </div>
          <div>
            <Label>New time</Label>
            <Select value={slotTime} onValueChange={setSlotTime}>
              <SelectTrigger data-testid="reschedule-time"><SelectValue /></SelectTrigger>
              <SelectContent>{SLOT_TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="reschedule-confirm" className="bg-ink hover:bg-ink/90 text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />} Confirm new slot
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
